import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import OrbLoader from "../common/OrbLoader";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  ClientAccountView,
  CompanyLinkOption,
  useLazySearchCompaniesForLinkQuery,
  useLinkCompanyToAccountMutation,
} from "../../store/apis/clientRegistryApi";

interface Props {
  account: ClientAccountView;
  canEdit: boolean;
}

/**
 * Las empresas enlazadas con una cuenta del registro.
 *
 * Existe porque el enlace es lo que hace que una propuesta llegue al CRM con empresa: las
 * 126 cuentas que se importaron de las hojas no traían `crmCompanyId`, así que el envío a
 * Twenty lo saca de la empresa enlazada.
 *
 * Son varias por cuenta a propósito: en la tabla de empresas una fila suele representar
 * cliente + MARCA. "12111-Ordesa-Blevit" es esta cuenta con la marca 11. Por eso cada
 * enlace lleva su número de marca, y por eso el enlace vive en la empresa y no aquí.
 */
const LinkedCompaniesPanel: React.FC<Props> = ({ account, canEdit }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchCompanies, search] = useLazySearchCompaniesForLinkQuery();
  const [linkCompany, { isLoading: isLinking }] =
    useLinkCompanyToAccountMutation();

  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<CompanyLinkOption | null>(null);
  const [brandNumber, setBrandNumber] = useState<string>("");

  // Desde 2 caracteres, que es lo que el backend exige, y con debounce.
  useEffect(() => {
    if (query.trim().length < 2) return;
    const handle = setTimeout(() => searchCompanies(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query, searchCompanies]);

  const toast = (message: string, severity: "success" | "error") =>
    dispatch(setToastMessage({ message, severity }));

  const errorMessage = (error: unknown) =>
    (error as { data?: { message?: string } })?.data?.message ??
    t("clientRegistry.linked.failed", "No se pudo cambiar el enlace");

  const handleLink = async () => {
    if (!picked) return;
    try {
      await linkCompany({
        companyId: picked.id,
        clientAccountId: account.id,
        clientBrandNumber: brandNumber === "" ? null : Number(brandNumber),
      }).unwrap();
      toast(t("clientRegistry.linked.done", "Empresa enlazada"), "success");
      setPicked(null);
      setQuery("");
      setBrandNumber("");
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const handleUnlink = async (companyId: string) => {
    try {
      await linkCompany({ companyId, clientAccountId: null }).unwrap();
      toast(
        t("clientRegistry.linked.undone", "Empresa desenlazada"),
        "success"
      );
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const withoutCrm = account.linkedCompanies.filter(
    (company) => !company.hasCrmCompany
  );

  return (
    <Box sx={{ py: 2, pl: { xs: 2, md: 6 }, pr: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        {t("clientRegistry.linked.title", "Empresas enlazadas")} (
        {account.linkedCompanies.length})
      </Typography>

      {account.linkedCompanies.length === 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {/* Es la causa concreta de que una propuesta salga al CRM sin empresa. */}
          {t(
            "clientRegistry.linked.none",
            "Sin empresa enlazada, las propuestas de este cliente llegan al CRM sin empresa y sin unidad de cuenta."
          )}
        </Alert>
      )}

      {withoutCrm.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t(
            "clientRegistry.linked.noCrm",
            "{{count}} de las empresas enlazadas no están en el CRM, así que no sirven para enganchar la propuesta.",
            { count: withoutCrm.length }
          )}
        </Alert>
      )}

      <Stack gap={0.75} mb={2}>
        {account.linkedCompanies.map((company) => (
          <Stack
            key={company.id}
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: 2,
              px: 1.5,
              py: 0.75,
            }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {company.nameCommercial}
            </Typography>

            {company.clientBrandNumber !== null ? (
              <Chip
                size="small"
                variant="outlined"
                label={t("clientRegistry.linked.brand", "marca {{n}}", {
                  n: company.clientBrandNumber,
                })}
              />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                label={t("clientRegistry.linked.asClient", "el cliente")}
              />
            )}

            {!company.hasCrmCompany && (
              <Tooltip
                title={t(
                  "clientRegistry.linked.notInCrm",
                  "Esta empresa no está en el CRM"
                )}
              >
                <WarningAmberIcon
                  sx={{ fontSize: 18, color: theme.palette.warning.main }}
                />
              </Tooltip>
            )}

            {canEdit && (
              <Tooltip title={t("clientRegistry.linked.unlink", "Desenlazar")}>
                <IconButton
                  size="small"
                  onClick={() => handleUnlink(company.id)}
                  disabled={isLinking}
                >
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
      </Stack>

      {canEdit && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={1}
          alignItems="flex-start"
        >
          <Autocomplete
            size="small"
            sx={{ flex: 2, minWidth: 240 }}
            options={search.data?.data ?? []}
            value={picked}
            loading={search.isFetching}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.nameCommercial}
            onInputChange={(_, value, reason) => {
              if (reason === "input") setQuery(value);
            }}
            onChange={(_, value) => setPicked(value)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack direction="row" alignItems="center" gap={1} width="100%">
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {option.nameCommercial}
                  </Typography>
                  {!option.hasCrmCompany && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="warning"
                      label={t("clientRegistry.linked.noCrmShort", "sin CRM")}
                    />
                  )}
                  {/* Avisa ANTES de enlazar: enlazarla se la quita a la otra cuenta. */}
                  {option.clientAccountId &&
                    option.clientAccountId !== account.id && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="error"
                        label={t(
                          "clientRegistry.linked.takenShort",
                          "ya enlazada"
                        )}
                      />
                    )}
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("clientRegistry.linked.pick", "Enlazar una empresa")}
                helperText={t(
                  "clientRegistry.linked.pickHelp",
                  "Desde 2 letras. Busca por nombre comercial."
                )}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {search.isFetching && (
                        <OrbLoader size="inline" state="searching" />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
            <InputLabel id={`which-brand-${account.id}`}>
              {t("clientRegistry.linked.whichBrand", "Qué representa")}
            </InputLabel>
            <Select
              labelId={`which-brand-${account.id}`}
              value={brandNumber}
              label={t("clientRegistry.linked.whichBrand", "Qué representa")}
              onChange={(event) => setBrandNumber(event.target.value)}
            >
              <MenuItem value="">
                {t("clientRegistry.linked.asClient", "el cliente")}
              </MenuItem>
              {/* Los números de marca, sin repetir: la misma marca se repite entre
                  países con el mismo número. */}
              {[
                ...new Set(account.brands.map((brand) => brand.brandNumber)),
              ].map((number) => (
                <MenuItem key={number} value={String(number)}>
                  {t("clientRegistry.linked.brand", "marca {{n}}", {
                    n: number,
                  })}{" "}
                  ·{" "}
                  {
                    account.brands.find((brand) => brand.brandNumber === number)
                      ?.name
                  }
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<AddLinkIcon />}
            onClick={handleLink}
            disabled={!picked || isLinking}
            sx={{ mt: { md: 0.25 } }}
          >
            {t("clientRegistry.linked.link", "Enlazar")}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default LinkedCompaniesPanel;
